/**
 * MessageList Component
 * Scrollable container for chat messages with auto-scroll and welcome message
 */

import React, { useRef, useEffect, useState } from 'react';
import ChatMessage from './ChatMessage';
import TypingIndicator from './TypingIndicator';

/**
 * Robot icon for welcome message
 */
function RobotIcon() {
  return (
    <svg className="chatbot-welcome-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="10" rx="2"></rect>
      <circle cx="12" cy="5" r="2"></circle>
      <path d="M12 7v4"></path>
      <line x1="8" y1="16" x2="8" y2="16"></line>
      <line x1="16" y1="16" x2="16" y2="16"></line>
    </svg>
  );
}

/**
 * Down arrow icon for scroll button
 */
function ArrowDownIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  );
}

/**
 * Format date for separator
 */
function formatDateSeparator(date) {
  const now = new Date();
  const msgDate = new Date(date);
  
  const isToday = msgDate.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = msgDate.toDateString() === yesterday.toDateString();
  
  if (isToday) return 'Today';
  if (isYesterday) return 'Yesterday';
  
  return msgDate.toLocaleDateString([], { 
    weekday: 'long',
    month: 'short', 
    day: 'numeric' 
  });
}

/**
 * Check if two dates are on different days
 */
function isDifferentDay(date1, date2) {
  if (!date1 || !date2) return false;
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.toDateString() !== d2.toDateString();
}

/**
 * Welcome message component
 */
function WelcomeMessage() {
  return (
    <div className="chatbot-welcome">
      <RobotIcon />
      <h3 className="chatbot-welcome-title">Hi, I'm Bob!</h3>
      <p className="chatbot-welcome-text">
        Your Rapid Fusion PE320 expert. Ask me about materials, calibration, troubleshooting, or anything else!
      </p>
    </div>
  );
}

/**
 * Date separator component
 */
function DateSeparator({ date }) {
  return (
    <div className="chatbot-date-separator">
      <div className="chatbot-date-line"></div>
      <span className="chatbot-date-text">{formatDateSeparator(date)}</span>
      <div className="chatbot-date-line"></div>
    </div>
  );
}

/**
 * Main MessageList component
 */
function MessageList({ 
  messages = [], 
  isStreaming = false, 
  streamingMessage = null,
  toolsInUse = []
}) {
  const containerRef = useRef(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [userScrolled, setUserScrolled] = useState(false);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!userScrolled && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, streamingMessage, userScrolled]);

  // Handle scroll events to show/hide scroll button
  const handleScroll = () => {
    if (!containerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    
    setShowScrollBtn(!isNearBottom);
    setUserScrolled(!isNearBottom);
  };

  // Scroll to bottom when button clicked
  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth'
      });
      setUserScrolled(false);
    }
  };

  // If no messages and not streaming, show welcome
  if (messages.length === 0 && !isStreaming) {
    return (
      <div className="chatbot-messages" ref={containerRef}>
        <WelcomeMessage />
      </div>
    );
  }

  return (
    <div 
      className="chatbot-messages" 
      ref={containerRef}
      onScroll={handleScroll}
    >
      {messages.map((message, index) => {
        const showDateSeparator = index === 0 || 
          isDifferentDay(messages[index - 1]?.timestamp, message.timestamp);
        
        return (
          <React.Fragment key={message.id || index}>
            {showDateSeparator && message.timestamp && (
              <DateSeparator date={message.timestamp} />
            )}
            <ChatMessage message={message} />
          </React.Fragment>
        );
      })}
      
      {/* Tool usage indicator */}
      {toolsInUse.length > 0 && (
        <div className="chatbot-tool-indicator">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M12 6v6l4 2"></path>
          </svg>
          Using: {toolsInUse.join(', ')}
        </div>
      )}
      
      {/* Streaming message or typing indicator */}
      {isStreaming && streamingMessage ? (
        <ChatMessage 
          message={{ 
            role: 'assistant', 
            content: streamingMessage 
          }} 
          isStreaming={true}
        />
      ) : isStreaming ? (
        <TypingIndicator />
      ) : null}
      
      {/* Scroll to bottom button */}
      {showScrollBtn && (
        <button 
          className="chatbot-scroll-btn"
          onClick={scrollToBottom}
          type="button"
        >
          <ArrowDownIcon />
          New messages
        </button>
      )}
    </div>
  );
}

export default MessageList;
